import {defineNuxtPlugin, useRoute, useAuth, navigateTo, createError, useAuthUser} from '#imports'
import { USER, JOB_AD_PRICING_TYPES } from '~/constants/wadConstants'
import {computed} from "vue"

export default defineNuxtPlugin(() => {
  const { data } = useAuth()
  const { isAuthenticated } = useAuthUser()
  const route = useRoute()
  const currentAccountType = computed(() => data.value?.data?.account_type)

  const ACCOUNT_TYPES = {
    ADMIN: 'admin',
    DEVELOPER: 'developer',
    COMPANY: 'company',
  }

  const wadAcl = {
    async requireGuest() {
      if (isAuthenticated) {
        return navigateTo(data.value?.user?.home?.url ?? '/')
      }
    },
    async requireLoggedIn() {
      if (!isAuthenticated) {
        window.open(`${wadPlatformBaseUrl}/en/user/login?redirect=${route.path}`)
      }
    },
    async requireCompany() {
      if (!isAuthenticated) {
        window.open(`${wadPlatformBaseUrl}/en/user/login?redirect=${route.path}`)
      }
      if (!wadAcl.isCompany()) {
        throw createError({ statusCode: 403, statusMessage: 'Access denied' })
      }
    },
    async requireDeveloper() {
      if (!isAuthenticated) {
        window.open(`${wadPlatformBaseUrl}/en/user/login?redirect=${route.path}`)
      }
      if (!wadAcl.isDeveloper()) {
        throw createError({ statusCode: 403, statusMessage: 'Access denied' })
      }
    },
    requirePermission(permission: string | string[]) {
      if (!isAuthenticated) {
        window.open(`${wadPlatformBaseUrl}/en/user/login?redirect=${route.path}`)
      }
      const permissions = typeof permission === 'string' ? [permission] : permission
      const userPermissions = data.value?.user?.permissions ?? []
      if (!userPermissions.some(p => permissions.includes(p))) {
        throw createError({ statusCode: 403, statusMessage: 'Access denied' })
      }
      return null
    },
    hasPermission(permission: string | string[]) {
      const permissions = typeof permission === 'string' ? [permission] : permission
      const userPermissions = data.value?.user?.permissions ?? []
      return userPermissions.some(p => permissions.includes(p))
    },
    isCompany() {
      return currentAccountType.value === USER.ACCOUNT_TYPES.COMPANY
    },
    isDeveloper() {
      return currentAccountType.value === USER.ACCOUNT_TYPES.DEVELOPER
    },
    isAdmin() {
      return currentAccountType.value === USER.ACCOUNT_TYPES.ADMIN
    },
    isGeneric() {
      return currentAccountType.value === USER.ACCOUNT_TYPES.GENERIC
    },
    canExecuteJobAction(action: string, job: any, allowReadOnly = false) {
      switch (action) {
        case 'unpublish':
        case 'edit':
        case 'updateUrl':
        case 'delete':
        case 'repost':
        case 'archive':
        case 'duplicate':
          switch (currentAccountType.value) {
            case ACCOUNT_TYPES.ADMIN:
              return allowReadOnly
                ? wadAcl.hasPermission('admin.view-company-jobs')
                : wadAcl.hasPermission('admin.edit-company-jobs')
            case ACCOUNT_TYPES.COMPANY:
              const userCompanyId = data.value?.user?.company?.id ?? null
              return (
                wadAcl.hasPermission('owner.company.edit-jobs') &&
                userCompanyId &&
                userCompanyId === job?.company?.id
              )
          }
          return false
        case 'boost':
        case 'republishPph':
          return wadAcl.isAdmin() && (allowReadOnly ? wadAcl.hasPermission('admin.view-company-jobs') : wadAcl.hasPermission('admin.edit-company-jobs'))
        case 'extend':
          switch (currentAccountType.value) {
            case ACCOUNT_TYPES.ADMIN:
              return allowReadOnly ? wadAcl.hasPermission('admin.view-company-jobs') : wadAcl.hasPermission('admin.edit-company-jobs')
            case ACCOUNT_TYPES.COMPANY:
              const userCompanyId = data.value?.user?.company?.id ?? null
              return (wadAcl.hasPermission('owner.company.edit-jobs') && userCompanyId && userCompanyId === job?.company?.id && job?.price_type === JOB_AD_PRICING_TYPES.MEMBERSHIP)
          }
      }
      return false
    },
  }

  return {
    provide: {
      wadAcl,
    },
  }
})
